import React from "react";
import { Box, Typography, Paper, Container, Link } from "@mui/material";

const LAST_UPDATED = "April 28, 2026";
const COMPANY_NAME = "Cloud Job Manager";
const SUPPORT_EMAIL = "support@cloudjobmanager.com";

const theme = {
  primary: "#0f2b46",
  accent: "var(--amber)",
  textPrimary: "#0f2b46",
  textSecondary: "#444",
  bgPage: "#f8f9fa",
  cardBorder: "1px solid #e0e0e0",
};

const sectionTitleSx = {
  fontWeight: 600,
  color: theme.textPrimary,
  fontSize: "1.25rem",
  mt: 4,
  mb: 1.5,
  paddingBottom: "6px",
  borderBottom: `2px solid ${theme.accent}`,
  display: "inline-block",
};

const bodySx = {
  color: theme.textSecondary,
  lineHeight: 1.75,
  fontSize: "1.05rem",
  mb: 1.5,
};

export default function TermsOfService() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: theme.bgPage,
        py: { xs: 4, sm: 6 },
        px: 2,
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <Container maxWidth="md" disableGutters>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: "12px",
            border: theme.cardBorder,
            boxShadow: "var(--shadow-lg)",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.textPrimary,
              fontSize: { xs: "1.75rem", sm: "2.1rem" },
              paddingBottom: "10px",
              borderBottom: `2px solid ${theme.accent}`,
              display: "inline-block",
              mb: 2,
            }}
          >
            Terms of Service
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.textSecondary,
              fontSize: "0.95rem",
              mb: 3,
            }}
          >
            Last updated: {LAST_UPDATED}
          </Typography>

          <Typography sx={bodySx}>
            Welcome to {COMPANY_NAME}. These Terms of Service ("Terms") govern
            your access to and use of the {COMPANY_NAME} website, applications,
            and related services (collectively, the "Service"). By creating an
            account or otherwise using the Service, you agree to be bound by
            these Terms. If you do not agree, please do not use the Service.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            1. Eligibility and Accounts
          </Typography>
          <Typography sx={bodySx}>
            You must be at least 18 years old and able to form a binding
            contract to use the Service. You are responsible for maintaining
            the confidentiality of your login credentials and for all activity
            that occurs under your account. Notify us immediately at{" "}
            <Link href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: theme.primary }}>
              {SUPPORT_EMAIL}
            </Link>{" "}
            if you suspect unauthorized access.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            2. Acceptable Use
          </Typography>
          <Typography sx={bodySx}>
            You agree not to misuse the Service. You will not (a) violate
            applicable law, (b) infringe the rights of others, (c) attempt to
            gain unauthorized access to the Service or related systems, (d)
            interfere with or disrupt the Service, (e) upload malicious code,
            or (f) use the Service to send spam, harassing, or unlawful
            communications.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            3. Customer Data
          </Typography>
          <Typography sx={bodySx}>
            You retain all rights to the data you submit to the Service
            ("Customer Data"). You grant {COMPANY_NAME} a limited license to
            host, copy, transmit, and display Customer Data solely as necessary
            to provide and improve the Service. You are responsible for the
            accuracy, quality, and legality of your Customer Data and for
            obtaining any consents required to provide it to us.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            4. Subscriptions, Fees, and Payment
          </Typography>
          <Typography sx={bodySx}>
            Paid plans are billed in advance on a recurring basis (monthly or
            annually) and are non-refundable except where required by law.
            Fees, taxes, and applicable payment processing charges are
            disclosed at checkout. We may change pricing for future billing
            cycles upon reasonable notice. Failure to pay may result in
            suspension or termination of your account.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            5. Third-Party Services
          </Typography>
          <Typography sx={bodySx}>
            The Service may integrate with third-party products (for example,
            payment gateways, email providers, and mapping services). Your use
            of those products is subject to their own terms and privacy
            policies. {COMPANY_NAME} is not responsible for third-party
            services.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            6. Intellectual Property
          </Typography>
          <Typography sx={bodySx}>
            The Service, including all software, design, text, and trademarks,
            is owned by {COMPANY_NAME} or its licensors and is protected by
            applicable intellectual property laws. Except for the limited
            rights expressly granted to you in these Terms, no other rights
            are granted.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            7. Termination
          </Typography>
          <Typography sx={bodySx}>
            You may cancel your subscription at any time from your account
            settings. We may suspend or terminate your access if you breach
            these Terms or if your use poses a risk to the Service or other
            users. Upon termination, your right to use the Service ceases
            immediately. Sections that by their nature should survive will
            survive termination.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            8. Disclaimers
          </Typography>
          <Typography sx={bodySx}>
            The Service is provided "as is" and "as available" without
            warranties of any kind, whether express or implied, including
            warranties of merchantability, fitness for a particular purpose,
            and non-infringement. {COMPANY_NAME} does not warrant that the
            Service will be uninterrupted, secure, or error-free.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            9. Limitation of Liability
          </Typography>
          <Typography sx={bodySx}>
            To the maximum extent permitted by law, {COMPANY_NAME} and its
            affiliates will not be liable for any indirect, incidental,
            special, consequential, or punitive damages, or any loss of
            profits, revenue, data, or goodwill, arising out of or in
            connection with the Service. Our aggregate liability for any
            claim related to the Service will not exceed the amounts paid by
            you to {COMPANY_NAME} in the twelve (12) months preceding the
            claim.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            10. Indemnification
          </Typography>
          <Typography sx={bodySx}>
            You agree to defend, indemnify, and hold harmless {COMPANY_NAME}
            from any claims, damages, liabilities, and expenses arising out of
            your use of the Service, your Customer Data, or your violation of
            these Terms.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            11. Changes to These Terms
          </Typography>
          <Typography sx={bodySx}>
            We may update these Terms from time to time. If we make material
            changes, we will provide notice through the Service or by email.
            Your continued use of the Service after the effective date of any
            update constitutes your acceptance of the revised Terms.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            12. Governing Law
          </Typography>
          <Typography sx={bodySx}>
            These Terms are governed by the laws of the jurisdiction in which
            {" "}{COMPANY_NAME} is headquartered, without regard to its conflict
            of laws principles. The exclusive venue for any dispute arising
            from these Terms or the Service will be the state or federal
            courts located in that jurisdiction.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            13. Contact
          </Typography>
          <Typography sx={bodySx}>
            Questions about these Terms? Reach us at{" "}
            <Link href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: theme.primary }}>
              {SUPPORT_EMAIL}
            </Link>
            .
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
